import { useEffect, useState } from "react";
import api, { formatBRL } from "@/lib/api";
import { Gift, Copy, Share2, Wallet, Users, Clock, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals/me").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const shareUrl = `${window.location.origin}/cadastro?ref=${data.referral_code}`;
  const ratePct = Math.round((data.cashback_rate || 0.10) * 100);

  const copyCode = () => {
    navigator.clipboard.writeText(data.referral_code);
    toast.success("Código copiado!");
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link de indicação copiado!");
  };
  const shareNative = async () => {
    const text = `Use meu código ${data.referral_code} no Solimões Card e ganhe descontos em saúde, farmácias e exames!`;
    if (navigator.share) {
      try { await navigator.share({ title: "Solimões Card", text, url: shareUrl }); } catch {}
    } else { copyLink(); }
  };
  const redeem = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/referrals/redeem");
      toast.success(`Cashback de ${formatBRL(r.applied)} aplicado na próxima fatura!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar cashback");
    } finally { setBusy(false); }
  };

  return (
    <section className="card-soft overflow-hidden" data-testid="referral-card">
      <div className="grid lg:grid-cols-5">
        {/* Left visual side */}
        <div className="lg:col-span-2 p-7 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0E2A5C 0%, #2A8FE0 100%)" }}>
          <div aria-hidden className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)" }} />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-bold uppercase tracking-widest backdrop-blur">
              <Gift className="h-3.5 w-3.5" /> Programa de Indicação
            </div>
            <h2 className="font-display text-3xl font-black mt-4 leading-tight">Indique e ganhe<br/>{ratePct}% de cashback</h2>
            <p className="text-sm text-white/85 mt-3">A cada amigo que assinar e pagar a primeira fatura, você ganha {ratePct}% do valor como crédito automático.</p>

            <div className="mt-6 rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <div className="text-[10px] uppercase tracking-widest opacity-75">Seu código de indicação</div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="font-mono text-2xl font-black tracking-widest" data-testid="referral-code">{data.referral_code || "—"}</span>
                <button onClick={copyCode} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="copy-referral-code">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#0E2A5C] text-sm font-semibold hover:bg-slate-100 transition" data-testid="copy-referral-link">
                <Copy className="h-3.5 w-3.5" /> Copiar link
              </button>
              <button onClick={shareNative} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/40 text-white text-sm font-semibold hover:bg-white/10 transition" data-testid="share-referral">
                <Share2 className="h-3.5 w-3.5" /> Compartilhar
              </button>
            </div>
          </div>
        </div>

        {/* Right metrics side */}
        <div className="lg:col-span-3 p-7">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-100 p-4" data-testid="cashback-balance-tile">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500"><Wallet className="h-3.5 w-3.5" /> Saldo</div>
              <div className="font-display text-2xl font-black text-[#2A8FE0] mt-1">{formatBRL(data.cashback_balance)}</div>
              <div className="text-[11px] text-slate-500">disponível</div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-4" data-testid="referrals-count-tile">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500"><Users className="h-3.5 w-3.5" /> Indicados</div>
              <div className="font-display text-2xl font-black text-[#0E2A5C] mt-1">{data.indicados_total}</div>
              <div className="text-[11px] text-slate-500">{data.indicados_creditados} pagaram • {data.indicados_pendentes} pendente(s)</div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-4" data-testid="cashback-earned-tile">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500"><Sparkles className="h-3.5 w-3.5" /> Total ganho</div>
              <div className="font-display text-2xl font-black text-emerald-600 mt-1">{formatBRL(data.total_creditado)}</div>
              <div className="text-[11px] text-slate-500">acumulado</div>
            </div>
          </div>

          <button
            onClick={redeem}
            disabled={busy || (data.cashback_balance || 0) <= 0}
            className="btn-primary w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="redeem-cashback"
          >
            {busy ? "Aplicando..." : `Usar ${formatBRL(data.cashback_balance)} na próxima fatura`}
          </button>

          <div className="mt-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Histórico de indicações</div>
            <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="referrals-list">
              {(data.referrals || []).length === 0 && (
                <div className="py-6 text-center text-sm text-slate-500">
                  Você ainda não indicou ninguém. Compartilhe seu código e comece a ganhar!
                </div>
              )}
              {(data.referrals || []).map((r) => (
                <div key={r.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`referral-item-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0E2A5C] text-sm truncate">{r.referred_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.referred_email}</div>
                  </div>
                  {r.status === "creditado" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 whitespace-nowrap">
                      <CheckCircle2 className="h-3 w-3" /> +{formatBRL(r.cashback_amount)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">
                      <Clock className="h-3 w-3" /> Aguardando 1ª fatura
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
